import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ProfileCompletenessTier } from '@paladin-monorepo/common';
import { profileApi } from '@/lib/api/profile';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Circle, ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';

function tierVariant(
  tier: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (tier) {
    case ProfileCompletenessTier.TierA:
      return 'default';
    case ProfileCompletenessTier.TierB:
      return 'secondary';
    case ProfileCompletenessTier.TierC:
      return 'outline';
    default:
      return 'destructive';
  }
}

export interface ProfileCompletenessCardProps {
  enabled?: boolean;
  className?: string;
}

export function ProfileCompletenessCard({
  enabled = true,
  className,
}: ProfileCompletenessCardProps) {
  const { t } = useTranslation();
  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileApi.getProfile(),
    enabled,
  });

  const completeness = profile?.completeness;

  if (!enabled || isLoading || !completeness) {
    return null;
  }

  const { breakdown, tier, nudges } = completeness;

  if (breakdown.totalPercent >= 100) {
    return null;
  }

  const topNudges = nudges.slice(0, 5);

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ListChecks className="h-5 w-5" />
              {t('profileCompleteness.title')}
            </CardTitle>
            <CardDescription>{t('profileCompleteness.subtitle')}</CardDescription>
          </div>
          <Badge variant={tierVariant(tier)}>{t(`profileCompleteness.tier.${tier}`)}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('profileCompleteness.scoreLabel')}</span>
            <span className="font-medium tabular-nums">{breakdown.totalPercent} %</span>
          </div>
          <Progress value={breakdown.totalPercent} className="h-2" />
        </div>
        <p className="text-muted-foreground text-sm">
          {t(`profileCompleteness.tierHint.${tier}`)}
        </p>
        {topNudges.length > 0 && (
          <ul className="space-y-2 text-sm">
            {topNudges.map((n) => (
              <li key={n.id} className="flex gap-2">
                <Circle
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <span>
                  {t(`profileCompleteness.nudges.${n.id}`, {
                    points: n.pointsAvailable,
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
        <Button variant="outline" size="sm" asChild>
          <Link to="/settings/profile">{t('profileCompleteness.ctaProfile')}</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
